import type {
  HouseConfiguration,
  HouseModelOption,
  LayoutOption,
  MaterialOption,
  ProductOption,
  StepId
} from "./types";

export const steps: Array<{ id: StepId; label: string; shortLabel: string }> = [
  { id: "model", label: "Husmodel", shortLabel: "Model" },
  { id: "layout", label: "Størrelse & plan", shortLabel: "Plan" },
  { id: "exterior", label: "Udvendigt", shortLabel: "Ude" },
  { id: "interior", label: "Indvendigt", shortLabel: "Inde" },
  { id: "options", label: "Tilvalg", shortLabel: "Tilvalg" },
  { id: "summary", label: "Opsummering", shortLabel: "Klar" }
];

export const houseModels: HouseModelOption[] = [
  {
    id: "classic-a",
    name: "Classic",
    tagline: "Det tidløse længehus",
    baseAreaM2: 128,
    basePrice: 1_795_000,
    shape: "long"
  },
  {
    id: "modern",
    name: "Modern",
    tagline: "Stram og enkel arkitektur",
    baseAreaM2: 128,
    basePrice: 1_895_000,
    shape: "compact",
    demo: true
  },
  {
    id: "l-shape",
    name: "L-Shape",
    tagline: "Læ omkring terrassen",
    baseAreaM2: 144,
    basePrice: 2_095_000,
    shape: "angle",
    demo: true
  },
  {
    id: "custom",
    name: "Custom",
    tagline: "Et personligt udgangspunkt",
    baseAreaM2: 144,
    basePrice: 2_245_000,
    shape: "angle",
    demo: true
  }
];

export const sizes = [96, 112, 128, 144, 160];

export const layouts: LayoutOption[] = [
  {
    id: "layout-a",
    name: "Plan A",
    bedrooms: 3,
    bathrooms: 2,
    description: "Lys opholdszone og tre gode værelser",
    priceDelta: 0,
    minArea: 96
  },
  {
    id: "layout-b",
    name: "Plan B",
    bedrooms: 4,
    bathrooms: 2,
    description: "Fire værelser og separat forældreafdeling",
    priceDelta: 48_000,
    minArea: 128
  },
  {
    id: "layout-c",
    name: "Plan C",
    bedrooms: 5,
    bathrooms: 3,
    description: "Fem værelser, tre bad og hjemmekontor",
    priceDelta: 128_000,
    minArea: 144
  }
];

export const facadeMaterials: MaterialOption[] = [
  { id: "wood-natural", name: "Naturtræ", category: "facade", color: "#8f7150", texture: "wood", priceDelta: 0 },
  { id: "wood-black", name: "Sort træ", category: "facade", color: "#252722", texture: "wood", priceDelta: 34_000 },
  { id: "wood-light", name: "Lys varmebehandlet", category: "facade", color: "#b49a77", texture: "wood", priceDelta: 49_000 },
  { id: "brick-grey", name: "Grå tegl", category: "facade", color: "#7c7972", texture: "stone", priceDelta: 95_000 }
];

export const roofMaterials: MaterialOption[] = [
  { id: "steel-black", name: "Sort ståltag", category: "roof", color: "#202421", texture: "metal", priceDelta: 0 },
  { id: "steel-grey", name: "Gråt ståltag", category: "roof", color: "#555b5a", texture: "metal", priceDelta: 22_000 },
  { id: "tile-black", name: "Sort tagsten", category: "roof", color: "#2c2b29", texture: "stone", priceDelta: 68_000 },
  { id: "green-roof", name: "Sedumtag", category: "roof", color: "#566849", texture: "stone", priceDelta: 148_000 }
];

export const windowMaterials: MaterialOption[] = [
  { id: "alu-black", name: "Sort aluminium", category: "window", color: "#171a18", texture: "metal", priceDelta: 0 },
  { id: "alu-bronze", name: "Bronze aluminium", category: "window", color: "#625347", texture: "metal", priceDelta: 26_000 },
  { id: "alu-white", name: "Hvid aluminium", category: "window", color: "#e7e5df", texture: "metal", priceDelta: 12_000 }
];

export const floorMaterials: MaterialOption[] = [
  { id: "oak-natural", name: "Eg natur", category: "floor", color: "#b89462", texture: "wood", priceDelta: 0 },
  { id: "oak-white", name: "Hvidolieret eg", category: "floor", color: "#c8b697", texture: "wood", priceDelta: 42_000 },
  { id: "concrete-light", name: "Lys microcement", category: "floor", color: "#aaa9a3", texture: "stone", priceDelta: 58_000 }
];

export const wallPackages: MaterialOption[] = [
  { id: "wall-soft-white", name: "Blød hvid", category: "wall", color: "#eeeae0", priceDelta: 0 },
  { id: "wall-nordic", name: "Nordisk palette", category: "wall", color: "#bdc2b8", priceDelta: 18_000 },
  { id: "wall-warm", name: "Varm palette", category: "wall", color: "#c9b9a3", priceDelta: 18_000 }
];

export const kitchenPackages: MaterialOption[] = [
  { id: "kitchen-core", name: "Core", category: "kitchen", color: "#dedbd3", priceDelta: 0 },
  { id: "kitchen-nordic", name: "Nordic", category: "kitchen", color: "#a6a594", priceDelta: 78_000 },
  { id: "kitchen-atelier", name: "Atelier", category: "kitchen", color: "#4b4f46", priceDelta: 142_000 }
];

export const bathroomPackages: MaterialOption[] = [
  { id: "bath-calm", name: "Calm", category: "bathroom", color: "#d4d0c8", priceDelta: 0 },
  { id: "bath-stone", name: "Stone", category: "bathroom", color: "#89877f", priceDelta: 54_000 },
  { id: "bath-spa", name: "Spa", category: "bathroom", color: "#68746c", priceDelta: 112_000 }
];

export const productOptions: ProductOption[] = [
  { id: "heat-pump", name: "Luft-til-vand varmepumpe", description: "Effektiv opvarmning året rundt", price: 82_000, category: "energy" },
  { id: "floor-heating", name: "Gulvvarme", description: "Komfort i alle opholdsrum", price: 64_000, category: "comfort" },
  { id: "solar", name: "Solcellepakke", description: "8 kWp med diskret integration", price: 118_000, category: "energy", requiresRoof: "steel-black" },
  { id: "insulation-plus", name: "Energi+", description: "Ekstra isolering og tæthedspakke", price: 76_000, category: "energy" },
  { id: "acoustic", name: "Akustikloft", description: "Træbeton i køkken og opholdsrum", price: 52_000, category: "comfort" },
  { id: "smart-home", name: "Smart home", description: "Lys, varme og adgang i én app", price: 46_000, category: "comfort" },
  { id: "panorama", name: "Panoramavinduer", description: "Større glaspartier mod haven", price: 88_000, category: "outdoor" }
];

export const defaultConfiguration: HouseConfiguration = {
  id: "cfg-demo",
  schemaVersion: 1,
  modelId: "classic-a",
  areaM2: 128,
  layoutId: "layout-a",
  bedrooms: 3,
  bathrooms: 2,
  office: false,
  utilityRoom: true,
  separateEntrance: false,
  facadeMaterialId: "wood-natural",
  roofMaterialId: "steel-black",
  windowColorId: "alu-black",
  doorId: "door-oak",
  terrace: true,
  carport: false,
  floorMaterialId: "oak-natural",
  wallPackageId: "wall-soft-white",
  kitchenPackageId: "kitchen-core",
  bathroomPackageId: "bath-calm",
  options: ["floor-heating"]
};

export const formatPrice = (price: number) =>
  new Intl.NumberFormat("da-DK", {
    style: "currency",
    currency: "DKK",
    maximumFractionDigits: 0
  }).format(price);
